import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { PatientBookingContext } from "./context/PatientBookingProvider";
import { BackendDoctor } from "./services";
import { mapBackendDoctorToSearchParam } from "./types";
import { base } from "../../../constants";
import PageRouterList from "@routes/routeList";

type DoctorSelectDropdownProps = {
  doctors: BackendDoctor[];
  loading?: boolean;
};

function DoctorSelectDropdown({ doctors, loading }: DoctorSelectDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { bookingSearchParam, setBookingSearchParams, copyId } =
    useContext(PatientBookingContext);

  const selectedLabel = bookingSearchParam.doctor
    ? `Dr. ${bookingSearchParam.doctor.doctorName}`
    : bookingSearchParam.department
      ? bookingSearchParam.department.departmentName
      : "Doctor waiting list";

  const handleClose = () => setAnchorEl(null);

  const handleOpenQueue = (doctorId: number) => {
    handleClose();
    window.open(
      `${base}ui/${PageRouterList.doctorQueue}/${doctorId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  useEffect(() => {
    if (copyId.departmentId && copyId.doctorId) {
      const doctor = doctors.find((doc) => doc.doctorId === copyId.doctorId);
      if (doctor) {
        setBookingSearchParams((prev) => ({
          ...prev,
          department: {
            departmentId: doctor.departmentId ?? copyId.departmentId ?? 0,
            departmentName:
              doctor.departmentName?.trim() ||
              prev.department?.departmentName ||
              "",
          },
          doctor: mapBackendDoctorToSearchParam(doctor),
        }));
      }
    }
  }, [copyId, doctors, setBookingSearchParams]);

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        fullWidth
        endIcon={<ArrowDropDownIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={loading}
        sx={{ textTransform: "none", justifyContent: "space-between" }}
      >
        {loading ? "Loading doctors..." : selectedLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: "70vh",
            overflowY: "auto",
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Doctor waiting list
          </Typography>
        </Box>

        {loading && (
          <MenuItem disabled>
            <ListItemIcon>
              <CircularProgress size={18} />
            </ListItemIcon>
            <ListItemText primary="Loading doctors..." />
          </MenuItem>
        )}

        {!loading &&
          doctors.map((doctor) => (
            <MenuItem
              key={doctor.doctorId}
              onClick={() => handleOpenQueue(doctor.doctorId)}
            >
              <ListItemText
                primary={`Dr. ${doctor.doctorName}`}
                secondary={doctor.departmentName?.trim()}
              />
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  bgcolor: doctor.color || "#ccc",
                  flexShrink: 0,
                  ml: 1,
                }}
              />
            </MenuItem>
          ))}

        {!loading && doctors.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No doctors available" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default DoctorSelectDropdown;
